import { useApolloClient, useSuspenseQuery } from '@apollo/client';
import { Suspense } from 'react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Card,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Text,
  cn,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';

import { GetTenantsList } from '../operations';

const listItemClassName = cn(
  'w-full min-w-[320px] grid items-center gap-2',
  'md:grid-cols-[40px_1fr_2fr_40px] lg:grid-cols-[40px_1fr_1fr_40px]',
  'grid-cols-[40px_1fr_40px]',
);

const listItemInfoClassName = cn(
  'grid grid-cols-3 gap-2 items-center justify-items-center text-text-supporting',
  '*:text-center *:whitespace-nowrap',
  'md:row-start-auto md:col-span-1 md:border-t-0 md:pt-0 md:text-text-tertiary',
  'row-start-2 col-span-3 border-t border-border-secondary pt-2',
);

interface TenantListItemProps {
  name: React.ReactNode;
  role: React.ReactNode;
  memberSince: React.ReactNode;
  tenantCreatedAt: React.ReactNode;
  isPendingApproval: boolean;
}

const TenantListItem: React.FC<TenantListItemProps> = ({
  name,
  role,
  memberSince,
  tenantCreatedAt,
  isPendingApproval,
}) => {
  return (
    <Card className={cn('px-2 py-3 md:py-4', listItemClassName)}>
      <Stack
        align="center"
        justify="center"
        fullWidth={false}
        className="mr-1 border-r border-r-bg-quaternary pr-1"
      >
        <Icon
          name="User"
          size="xl"
          className="text-bg-accent-strong-secondary/80"
        />
      </Stack>
      <Stack gap={2} direction="horizontal">
        <Heading size="lg" weight="semibold">
          {name}
        </Heading>
        {isPendingApproval && (
          <Badge className="pointer-events-none bg-bg-warning-strong-primary text-text-warning-strong">
            Pending Approval
          </Badge>
        )}
      </Stack>
      <div className={listItemInfoClassName}>
        <Text>{role}</Text>
        <Stack
          className="justify-self-start md:justify-self-center"
          direction="horizontal"
          fullWidth={false}
          gap={2}
        >
          <Icon
            name="ContactRound"
            size="lg"
            className="text-text-supporting md:hidden"
          />
          <Text>{memberSince}</Text>
        </Stack>
        <Stack
          className="justify-self-start md:justify-self-center"
          direction="horizontal"
          fullWidth={false}
          gap={2}
        >
          <Icon
            name="CalendarClock"
            size="lg"
            className="text-text-supporting md:hidden"
          />
          <Text>{tenantCreatedAt}</Text>
        </Stack>
      </div>
      <Icon
        name="ChevronRight"
        size="xl"
        className="h-6 w-10 border-l-2 border-l-bg-quaternary pl-2 text-bg-accent-strong-secondary"
      />
    </Card>
  );
};

const TenantsListContent: React.FC = () => {
  const { data } = useSuspenseQuery(GetTenantsList);
  const client = useApolloClient();

  return (
    <>
      {data.assignedTenantUsers.map((tenant) => {
        if (!tenant.tenantId) {
          return null;
        }

        return (
          <Link
            to={tenant.tenantId}
            key={tenant.tenantId}
            className={cn(
              'group drop-shadow-none hover:drop-shadow-lg',
              'transition-all duration-200 ease-in-out',
            )}
            onClick={() => {
              // Clear the apollo cache if we're switching tenants
              client.resetStore();
            }}
          >
            <TenantListItem
              name={tenant.name}
              role={tenant.role}
              memberSince={formatToLocale(tenant.memberSince, { noTime: true })}
              tenantCreatedAt={formatToLocale(tenant.tenantCreatedAt, {
                noTime: true,
              })}
              isPendingApproval={tenant.isPendingApproval ?? false}
            />
          </Link>
        );
      })}
    </>
  );
};

export const TenantsList = () => {
  return (
    <Stack className="max-h-full overflow-auto p-2" gap={2} align="stretch">
      <div className={cn('hidden! px-5 py-1 md:grid!', listItemClassName)}>
        <div />
        <Text>Organization</Text>
        <div className={listItemInfoClassName}>
          <Stack direction="horizontal" fullWidth={false} gap={4}>
            <Icon name="UserCog" size="lg" className="text-text-supporting" />
            <Text>Role</Text>
          </Stack>
          <Stack direction="horizontal" fullWidth={false} gap={4}>
            <Icon
              name="ContactRound"
              size="lg"
              className="text-text-supporting"
            />
            <Text>Member Since</Text>
          </Stack>
          <Stack direction="horizontal" fullWidth={false} gap={4}>
            <Icon
              name="CalendarClock"
              size="lg"
              className="text-text-supporting"
            />
            <Text>Created at</Text>
          </Stack>
        </div>
      </div>
      <Suspense
        fallback={
          <>
            {[...Array(4)].map((_, index) => (
              <TenantListItem
                key={index}
                name={<Skeleton className="h-4 w-[200px] bg-border-primary" />}
                role={
                  <Stack align="center" justify="center">
                    <Skeleton className="h-4 w-[70px] bg-border-primary" />
                  </Stack>
                }
                memberSince={
                  <Stack align="center" justify="center">
                    <Skeleton className="h-4 w-[70px] bg-border-primary" />
                  </Stack>
                }
                tenantCreatedAt={
                  <Stack align="center" justify="center">
                    <Skeleton className="h-4 w-[70px] bg-border-primary" />
                  </Stack>
                }
                isPendingApproval={false}
              />
            ))}
          </>
        }
      >
        <TenantsListContent />
      </Suspense>
    </Stack>
  );
};
